import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';

import { goldGlow } from '../../../../shared/glowStyles';
import { Pressable } from '../../common/Touchable';
import { inventoryColors } from '../../Inventory/inventoryTheme';
import {
	FRIENDS_FONT,
	friendsColors,
	hairline,
	inset,
	RADIUS_LG,
	RADIUS_MD,
} from '../friendsTheme';
import { getHowItWorksCopy } from './inviteFriendsCatalog';

/**
 * The spec's "How it works?" modal: five numbered steps over the Invite Friends panel. It sits
 * inside that panel (absolute, with the modal's own scrim) rather than as a native <Modal>, the
 * same way the profile tab is rendered inline, so it never stacks a second native modal.
 */
type Props = {
	scale: number;
	requiredLevel: number;
	onClose: () => void;
};

export const HowItWorksOverlay = ({ scale, requiredLevel, onClose }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const copy = useMemo(() => getHowItWorksCopy(t), [t]);

	return (
		<View style={styles.root}>
			<Pressable accessibilityRole='button' style={StyleSheet.absoluteFill} onPress={onClose} />

			<View style={styles.card}>
				<Text style={styles.title}>{copy.title}</Text>

				{copy.steps(requiredLevel).map((step, index) => (
					<View key={step.title} style={styles.step}>
						<View style={styles.badge}>
							<Text style={styles.badgeText}>{index + 1}</Text>
						</View>
						<View style={styles.stepCopy}>
							<Text style={styles.stepTitle}>{step.title}</Text>
							<Text style={styles.stepBody}>{step.body}</Text>
						</View>
					</View>
				))}

				<Pressable accessibilityRole='button' onPress={onClose} style={styles.button}>
					<Text style={styles.buttonLabel}>{copy.close}</Text>
				</Pressable>
			</View>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		root: {
			...StyleSheet.absoluteFillObject,
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: inventoryColors.scrim,
			zIndex: 4,
		},
		card: {
			width: 318 * scale,
			gap: 12 * scale,
			padding: inset(16, scale),
			borderRadius: RADIUS_LG * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surface,
		},
		title: {
			textAlign: 'center',
			color: friendsColors.accent,
			fontFamily: FRIENDS_FONT,
			fontSize: 20 * scale,
			fontWeight: '900',
			lineHeight: 26 * scale,
		},
		step: {
			flexDirection: 'row',
			gap: 10 * scale,
		},
		badge: {
			width: 24 * scale,
			height: 24 * scale,
			borderRadius: 12 * scale,
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: friendsColors.gold300,
		},
		badgeText: {
			color: friendsColors.onGold,
			fontFamily: FRIENDS_FONT,
			fontSize: 13 * scale,
			fontWeight: '900',
		},
		stepCopy: {
			flex: 1,
			gap: 2 * scale,
		},
		stepTitle: {
			color: friendsColors.gold50,
			fontFamily: FRIENDS_FONT,
			fontSize: 14 * scale,
			fontWeight: '700',
			lineHeight: 18 * scale,
		},
		stepBody: {
			color: friendsColors.muted,
			fontFamily: FRIENDS_FONT,
			fontSize: 12 * scale,
			fontWeight: '500',
			lineHeight: 16.8 * scale,
		},
		button: {
			height: 40 * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
			...goldGlow,
		},
		buttonLabel: {
			color: friendsColors.gold50,
			fontFamily: FRIENDS_FONT,
			fontSize: 15 * scale,
			fontWeight: '900',
		},
	});

export default HowItWorksOverlay;
